import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, Grid,
  List, ListItem, ListItemButton, ListItemText, alpha, IconButton, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getSenateJournal, getSenateJournalNavigation } from '../services/api';

const THEME_COLOR = '#c62828'; // Red for Senate

export default function SenateJournalsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTocItem, setActiveTocItem] = useState(null);
  const [navigation, setNavigation] = useState({ prev: null, next: null });

  // Refs for scroll tracking
  const contentRef = useRef(null);
  const isScrollingRef = useRef(false);

  useEffect(() => {
    const fetchDocument = async () => {
      setLoading(true);
      setError(null);
      try {
        const [data, nav] = await Promise.all([
          getSenateJournal(id),
          getSenateJournalNavigation(id).catch(() => ({ prev: null, next: null }))
        ]);
        setDocument(data);
        setNavigation(nav);
      } catch (err) {
        console.error('Error fetching Senate Journal:', err);
        setError('Failed to load Senate Journal');
      }
      setLoading(false);
    };
    fetchDocument();
  }, [id]);

  const handleNavigate = (docId) => {
    navigate(`/senate-journals/${docId}`);
  };

  const formatNavDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Extract TOC from plain text content - looking for numbered items
  // Senate journals have format like "1 Meeting of Senate ..... 3725"
  const tocItems = useMemo(() => {
    if (!document?.content) return [];

    const items = [];
    const lines = document.content.split('\n');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      // Match numbered sections like "1 Meeting of Senate" or "12 DOCUMENTS"
      // Also handle lines with page references like "1 Meeting of Senate ..... 3725"
      const numberedMatch = line.match(/^(\d+)\s+([A-Za-z].*?)(?:\s*\.{3,}.*)?$/);
      if (numberedMatch && numberedMatch[2].length > 3) {
        let title = numberedMatch[2].trim();
        // Remove trailing dots and page numbers
        title = title.replace(/\s*\.+\s*\d*$/, '');
        // Skip if it looks like a page number only
        if (!/^\d+$/.test(title)) {
          items.push({
            id: `toc-section-${i}`,
            number: numberedMatch[1],
            title: title.length > 60 ? title.substring(0, 60) + '...' : title,
            fullTitle: title,
            lineIndex: i
          });
        }
      }
    }

    return items;
  }, [document?.content]);

  // Helper to escape HTML
  const escapeHtml = (text) => {
    const div = window.document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Process content to add section anchors
  const processedContent = useMemo(() => {
    if (!document?.content || tocItems.length === 0) return document?.content || '';

    const lines = document.content.split('\n');
    const processedLines = lines.map((line, index) => {
      const item = tocItems.find(t => t.lineIndex === index);
      if (item) {
        // Wrap the line with a div that has the section ID
        return `<div id="${item.id}" class="toc-section-anchor" style="scroll-margin-top: 100px;">${escapeHtml(line)}</div>`;
      }
      return escapeHtml(line);
    });

    return processedLines.join('\n');
  }, [document?.content, tocItems]);


  // Scroll to a section when clicking TOC item
  const scrollToSection = useCallback((sectionId) => {
    isScrollingRef.current = true;
    setActiveTocItem(sectionId);

    const element = window.document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Reset scrolling flag after animation
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 1000);
    } else {
      isScrollingRef.current = false;
    }
  }, []);

  // Setup IntersectionObserver to track active section while scrolling
  useEffect(() => {
    if (tocItems.length === 0) return;

    // Set first item as active initially
    if (!activeTocItem && tocItems.length > 0) {
      setActiveTocItem(tocItems[0].id);
    }

    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -60% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      // Don't update if user clicked a TOC item (manual scroll in progress)
      if (isScrollingRef.current) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveTocItem(entry.target.id);
        }
      });
    }, observerOptions);

    // Observe all section anchors after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      tocItems.forEach((item) => {
        const element = window.document.getElementById(item.id);
        if (element) {
          observer.observe(element);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [tocItems, activeTocItem]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: THEME_COLOR }} />
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Document not found'}</Typography>
        <Button component={RouterLink} to="/senate-journals" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Senate Journals
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Navigation Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          component={RouterLink}
          to="/senate-journals"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Senate Journals
        </Button>

        {/* Prev/Next Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={navigation.prev ? `Previous: ${formatNavDate(navigation.prev.date)}` : 'No previous document'}>
            <span>
              <IconButton
                onClick={() => navigation.prev && handleNavigate(navigation.prev.id)}
                disabled={!navigation.prev}
                sx={{
                  bgcolor: navigation.prev ? alpha(THEME_COLOR, 0.1) : 'transparent',
                  '&:hover': { bgcolor: alpha(THEME_COLOR, 0.2) }
                }}
              >
                <NavigateBeforeIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            {formatDate(document?.date)}
          </Typography>
          <Tooltip title={navigation.next ? `Next: ${formatNavDate(navigation.next.date)}` : 'No next document'}>
            <span>
              <IconButton
                onClick={() => navigation.next && handleNavigate(navigation.next.id)}
                disabled={!navigation.next}
                sx={{
                  bgcolor: navigation.next ? alpha(THEME_COLOR, 0.1) : 'transparent',
                  '&:hover': { bgcolor: alpha(THEME_COLOR, 0.2) }
                }}
              >
                <NavigateNextIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(THEME_COLOR, 0.08)} 0%, ${alpha(THEME_COLOR, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: alpha(THEME_COLOR, 0.15)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <DescriptionIcon sx={{ fontSize: 48, color: THEME_COLOR }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: THEME_COLOR,
                mb: 0.5
              }}
            >
              {document.title || 'Senate Journal'}
            </Typography>

            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {formatDate(document.date)}
            </Typography>

            {/* Metadata chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                label="Senate"
                size="small"
                sx={{ bgcolor: THEME_COLOR, color: 'white' }}
              />
              {document.journal_number && (
                <Chip
                  label={`Journal No. ${document.journal_number}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {document.parliament_number && (
                <Chip
                  label={`${document.parliament_number}th Parliament`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {document.source_url && (
                <Button
                  size="small"
                  variant="outlined"
                  href={document.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon />}
                  sx={{ borderColor: THEME_COLOR, color: THEME_COLOR }}
                >
                  View on ParlInfo
                </Button>
              )}
              {document.pdf_url && (
                <Button
                  size="small"
                  variant="outlined"
                  href={document.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<PictureAsPdfIcon />}
                >
                  Download PDF
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Table of Contents */}
        {tocItems.length > 0 && (
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 80,
                maxHeight: 'calc(100vh - 120px)',
                overflow: 'auto'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: THEME_COLOR }}>
                Contents
              </Typography>
              <List dense disablePadding>
                {tocItems.map((item) => (
                  <ListItem key={item.id} disablePadding>
                    <ListItemButton
                      selected={activeTocItem === item.id}
                      onClick={() => scrollToSection(item.id)}
                      sx={{
                        borderLeft: activeTocItem === item.id
                          ? `3px solid ${THEME_COLOR}`
                          : '3px solid transparent',
                        py: 0.75
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: THEME_COLOR,
                                minWidth: '24px',
                                fontSize: '0.8rem'
                              }}
                            >
                              {item.number}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: activeTocItem === item.id ? 600 : 400
                              }}
                            >
                              {item.title}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Main Content */}
        <Grid item xs={12} md={tocItems.length > 0 ? 9 : 12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box
              ref={contentRef}
              sx={{
                '& .toc-section-anchor': {
                  scrollMarginTop: '100px',
                  fontWeight: 600,
                  color: THEME_COLOR,
                  mt: 2,
                  mb: 1
                },
                '& p': { mb: 2, lineHeight: 1.8 },
                '& pre': {
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  lineHeight: 1.6
                },
                lineHeight: 1.8,
                fontSize: '0.95rem'
              }}
            >
              {document.content_html ? (
                <div dangerouslySetInnerHTML={{ __html: document.content_html }} />
              ) : tocItems.length > 0 ? (
                <div
                  style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, lineHeight: 1.8 }}
                  dangerouslySetInnerHTML={{ __html: processedContent }}
                />
              ) : (
                <Typography
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    m: 0,
                    lineHeight: 1.8
                  }}
                >
                  {document.content}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
